import { Brand, Type } from '@lendi/lala-react';
import { State, TeamMemberSkill, TeamMemberSkillLGSF, TeamMemberSubSkillLGSF } from '@lendi/core-constants';
import { TeamMember } from '../interfaces';
import { InteractionType } from '@lendi/core-constants';

export enum ApptTeamType {
  Customer = 'Customer',
  Self = 'Self',
  SomeoneElse = 'Someone Else',
  SomeoneSpecific = 'Someone Specific',
  HotTransfer = 'Hot Transfer',
}

export enum AppointmentStatus {
  Allocated = 'Allocated',
  Cancelled = 'Cancelled',
  Rescheduled = 'Rescheduled',
  NoShow = 'No Show',
  AppointmentHeld = 'Appointment Held',
}

export enum TeamMemberTypeLGSF {
  Broker = 'Broker',
  SLR = 'SLR',
  SRM = 'SRM',
}

export enum TeamMemberType {
  Broker = 'Broker',
  BuyersAgent = 'Buyers Agent',
}

export interface Meta {
  actionedBy: string;
  actionedByRole: Type;
}

export interface Timeslot {
  start: string;
  end: string;
}

export interface Address {
  unit?: string;
  lotSection?: string;
  level?: string;
  buildingName?: string;
  streetNumber?: string;
  streetName?: string;
  streetType?: string;
  suburb?: string;
  city?: string;
  postcode?: string;
  state?: State | string;
  country?: string;
}

interface AddressWrapper {
  address: Address;
  formatString?: string;
}

export interface Customer {
  id: string;
  timezone: string;
  firstName?: string;
  lastName?: string;
  address?: AddressWrapper;
}

export interface CalendarUrls {
  ics?: string;
  google?: string;
  outlook?: string;
}
export interface Appointment extends Omit<CreateAppointmentRequest, 'brokerSfIds'> {
  id: string;
  eventId?: string;
  broker: TeamMember;
  appointmentStatus: AppointmentStatus;
  brand: Brand;
  meta: Meta;
  createdAt: string;
  updatedAt?: string;
  brokerNotes?: string;
  calendarUrls?: CalendarUrls;
  storeName?: string;
}

export interface CreateAppointmentRequest {
  brokerSfIds?: string[];
  timeslot: Timeslot;
  customer: Customer;
  interactionType: InteractionType;
  overrideUnavailability?: Boolean;
  apptType?: ApptTeamType;
  teamMemberType?: TeamMemberType;
  teamMemberTypeLGSF?: TeamMemberTypeLGSF;
  teamMemberSkills?: (TeamMemberSkill | TeamMemberSkillLGSF)[];
  teamMemberSubskills?: TeamMemberSubSkillLGSF[];
  instructionNotes?: string;
  storeId?: string;
  //this is used to indicate if the f2f(another location) appt is created through myself journey, we book video appt when there is no f2f available in brokers calendar
  selfInteractionOverride?: boolean;
  // this is used for CV to skip account owner checking when booking appointment if it is from store page.
  isFromStorePage?: boolean;
}

export type RescheduleAppointmentRequest = CreateAppointmentRequest;

export interface UpdateAppointmentRequest {
  brokerNotes: string;
  timeslot?: Timeslot;
  brand?: Brand;
  meta?: Meta;
  interactionType?: InteractionType;
}

export interface AvailabilityParams {
  type?: InteractionType;
  postcode?: string;
  suburb?: string;
  brokerId?: string;
  storeId?: string;
  isFromStorePage?: boolean;
  days?: number;
}

export interface V3AvailabilityParams extends AvailabilityParams {
  types?: string;
  suburb?: string;
}

export interface AvailabilityByTeamTypeParams {
  customerId: string;
  teamMemberTypeLGSF: string;
  appointmentType?: InteractionType;
  teamMemberSkills?: string[];
  teamMemberSubskills?: string[];
  types?: string;
  postcode?: string;
  suburb?: string;
  storeId?: string;
}

export interface User {
  id: string;
  brand: string;
  createdAt: string;
  updatedAt: string;
  email: string;
  isDeleted: boolean;
  mobileNumber: string;
  firstName?: string;
  lastName?: string;
  middleName?: string;
  gender?: string;
  title?: string;
  dateOfBirth?: string;
  externalId?: string;
  accountOrigin?: string;
  leadId?: string;
  emit?: boolean;
  emailVerified?: boolean;
  mobileVerified?: boolean;
  timezone?: string;
  postcode?: string;
  suburb?: string;
  storeId?: string;
}

export interface AccountOwner {
  brokerId: string;
  customerId: string;
  name: string;
  storeId?: string;
  previousOwnerId?: string;
  previousStoreId?: string;
  isNationalMarketplace?: boolean;
}

export enum PermissionActions {
  admin = 'admin',
  cancel = 'cancel',
  reallocate = 'reallocate',
  reschedule = 'reschedule',
}

export type AppointmentPermissions = {
  [key in PermissionActions]: boolean;
};

export interface CancelAppointmentDetails {
  cancellationReason: string;
  cancellationNotes: string;
}

export interface CancelAppointmentResponse extends Appointment {
  cancellationReason: string;
  cancellationNotes: string;
}

export enum EnquiryPurpose {
  Refinance = 'Refinance',
  NewPurchase = 'New Purchase',
  Investment = 'Investment',
  AppointmentRequest = 'Appointment Request',
  UrgentCallback = 'Urgent Callback',
  GeneralInfo = 'General Info',
  NonMortgageResearch = 'Non Mortgage Research',
  NonMortgageLoan = 'Non Mortgage Loan',
  Other = 'Other',
}

export enum CallbackSource {
  Callback = 'Call_back', // intentional lower capitalisation
  NoAvailabilities = 'No_availabilities',
}

export enum CallbackApptType {
  Callback = 'Call back',
}

export interface CallMeBackRequest {
  customer: Customer;
  enquiryPurpose: EnquiryPurpose;
  source: CallbackSource;
  brokerId?: string;
  storeId?: string;
}

export interface CallMeBackAppointment {
  id: string;
  customerId: string;
  customerTimezone: string;
  apptType: CallbackApptType;
  brand: Brand;
  source: CallbackSource;
  createdAt: string;
  brokerId?: string;
  storeId?: string;
}

export type CallMeBackAppointmentResponse = {
  data: CallMeBackAppointment;
  status: number;
};

export interface FetchFullAvailabilityRequest {
  teamMemberIds: string[];
}

export interface FetchFullAvailabilityResponse {
  status: number;
}
